<template>
  <div class="slider">
    
    <lightbox
      v-if="selectedImage"
      :image-url="selectedImage"
      @close="() => selectedImage = null"  
    />

    <div class="slider__wrapper">

      <button class="slider__control" @click="prevSlide">
        <span class="slider__control--left">
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd"
              d="M9.46991 6.30951C9.17702 6.60241 9.17702 7.07728 9.46991 7.37017L14.9396 12.8398L9.46991 18.3095C9.17702 18.6024 9.17702 19.0773 9.46991 19.3702C9.76281 19.6631 10.2377 19.6631 10.5306 19.3702L16.5306 13.3702C16.6712 13.2295 16.7502 13.0388 16.7502 12.8398C16.7502 12.6409 16.6712 12.4502 16.5306 12.3095L10.5306 6.30951C10.2377 6.01662 9.76281 6.01662 9.46991 6.30951Z"
              fill="#A8A8A8" />
          </svg>
        </span>
      </button>

      <div class="slider__inner" ref="slider">
        <TransitionGroup name="slide-swipe">
          <div
            class="slider__item"
            :class="[
              { 'slider__item--active': currentSlide === item.slide }
            ]"
            v-for="(item, itemOrder) in items"
            :key="`slider_item_${itemOrder}`"
            v-show="currentSlide === item.slide"
            @click="() => selectedImage = item.url"
          >
            <img class="slider__image" :src="item.url" :alt="`slider-item_${itemOrder}`" />
          </div>
        </TransitionGroup>
      </div>

      <button class="slider__control" @click="nextSlide">
        <span class="slider__control--right">
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd"
              d="M9.46991 6.30951C9.17702 6.60241 9.17702 7.07728 9.46991 7.37017L14.9396 12.8398L9.46991 18.3095C9.17702 18.6024 9.17702 19.0773 9.46991 19.3702C9.76281 19.6631 10.2377 19.6631 10.5306 19.3702L16.5306 13.3702C16.6712 13.2295 16.7502 13.0388 16.7502 12.8398C16.7502 12.6409 16.6712 12.4502 16.5306 12.3095L10.5306 6.30951C10.2377 6.01662 9.76281 6.01662 9.46991 6.30951Z"
              fill="#A8A8A8" />
          </svg>
        </span>
      </button>

    </div>

  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, type Ref } from 'vue';
import Hammer from 'hammerjs';

import Lightbox from './Lightbox.vue';

import type { ISliderImage } from '../../types/slider.types';

interface IProps {
  images: string[];
  max: number;
}
const props = defineProps<IProps>();

const currentSlide: Ref<number> = ref(0);
const items: Ref<ISliderImage[]> = ref([]);
const selectedImage: Ref<string | null> = ref(null);
const slider: Ref<HTMLDivElement | null> = ref(null);

watch(() => props.images, () => {
  setSliderItems();
}, { deep: true, immediate: true });

function nextSlide() {
  if (currentSlide.value < props.images.length / props.max - 1) {
    currentSlide.value += 1;
  }
}

function prevSlide() {
  if (currentSlide.value) {
    currentSlide.value -= 1;
  }
}

function setSliderItems() {
  let slide = -1;
  items.value = props.images.map((imageUrl, imageOrder) => {
    if (imageOrder % props.max === 0) {
      slide += 1;
    }
    return {
      url: imageUrl,
      slide,
    }
  });
}

onMounted(() => {
  if (slider.value) {
    const hammer = new Hammer(slider.value);

    hammer.on('swipeleft', () => {
      nextSlide();
    });
    hammer.on('swiperight', () => {
      prevSlide();
    });
  }
});
</script>

<style scoped lang="scss">
.slider {
  width: 100%;

  &__wrapper {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  &__inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  &__item {
    max-width: 373px;
    max-height: 373px;
    display: none;
    
    &--active {
      display: flex;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__control {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;

    &--left {
      transform: rotate(180deg);
    }

    &--right {
      transform: rotate(0deg);
    }
  }
}

.slide-swipe-enter-active,
.slide-swipe-leave-active {
  transition: all 0.5s ease;
}
.slide-swipe-enter-from,
.slide-swipe-leave-to {
  opacity: 0.5;
}

@media screen and (max-width: 768px) {
  .slider {
    &__inner {
      flex-wrap: wrap;
    }
  }
}
</style>